// ════════════════════════════════════════════════════════════════════════
//  Übertragen – eine Datei aus dieser Umgebung, eine Datei in diese Umgebung
//
//  Die Karte steht auf der LISTE der Lehrgänge und nicht in einem einzelnen.
//  Die Datei betrifft alle; in einem Lehrgang stünde ein Knopf, der die anderen
//  mitnimmt.
//
//  Beide Richtungen in einer Karte, weil es ein Vorgang mit zwei Enden ist:
//  hier herunterladen, dort hochladen. Zwei Karten nebeneinander sähen aus wie
//  zwei Funktionen, die nichts miteinander zu tun haben.
// ════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Problem } from "@otto/lehrgang";
import { useSession } from "@otto/plattform";
import { coursePath } from "../../../shared/lib/coursePath.js";
import {
  useExportCurricula,
  useImportCurricula,
  useLiveCurricula,
  type ImportReport,
} from "../lib/data.js";
import { isImportable, planImport, type ImportEntry } from "../lib/importPlan.js";
import {
  berlinDate,
  readTransfer,
  transferFileName,
  type TransferFile,
} from "../lib/transferFile.js";
import styles from "../../../shared/components/shell.module.css";

/**
 * Eine Datei anbieten.
 *
 * Über einen Blob und einen Anker, den niemand sieht: die Datei entsteht im
 * Browser, es gibt keine Adresse, unter der sie läge.
 */
function download(name: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "application/json" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Was der Eintrag hier bewirken wird – eine Zeile, die niemand deuten muss. */
function wirkung(entry: ImportEntry): string {
  if (!isImportable(entry)) return "kommt nicht an";
  return entry.isNew
    ? `wird angelegt, Entwurf als Fassung ${entry.nextVersion}`
    : `Entwurf als Fassung ${entry.nextVersion}`;
}

export function Transfer() {
  const { client } = useSession();
  const exportieren = useExportCurricula(client);

  const [datei, setDatei] = useState<TransferFile | null>(null);
  const [leseFehler, setLeseFehler] = useState<readonly Problem[]>([]);
  // Abgewählt statt gewählt: die Vorschau entsteht erst, wenn der Zustand
  // dieser Umgebung geladen ist. Eine Liste der Gewählten müsste danach
  // nachgezogen werden – eine Liste der Abgewählten ist von Anfang an richtig.
  const [abgewaehlt, setAbgewaehlt] = useState<ReadonlySet<string>>(new Set());
  const [bericht, setBericht] = useState<readonly ImportReport[] | null>(null);

  const hier = useLiveCurricula(client, datei !== null);
  const importieren = useImportCurricula(client);

  const plan = useMemo(
    () => (datei && hier.data ? planImport(datei, hier.data) : []),
    [datei, hier.data],
  );
  const gewaehlt = plan.filter((e) => isImportable(e) && !abgewaehlt.has(e.slug));

  // Über `FileReader` und nicht über `file.text()`: das kürzere gibt es in
  // jsdom nicht, und dann ließe sich genau der Weg nicht prüfen, den die
  // Redaktion geht. Beides ist in jedem Browser dasselbe.
  const dateiWaehlen = (file: File) => {
    setBericht(null);
    setAbgewaehlt(new Set());
    const reader = new FileReader();
    reader.onerror = () => {
      setDatei(null);
      setLeseFehler([{ path: "datei", message: "Die Datei war nicht zu lesen." }]);
    };
    reader.onload = () => {
      const gelesen = readTransfer(String(reader.result ?? ""));
      if ("problems" in gelesen) {
        setDatei(null);
        setLeseFehler(gelesen.problems);
        return;
      }
      setLeseFehler([]);
      setDatei(gelesen.file);
    };
    reader.readAsText(file);
  };

  return (
    // Die Marke an der Karte: „Übertragen“ ist ein Abschnitt mit zwei Hälften,
    // Herunterladen und Hochladen, und die Station meint beide.
    <div className={`${styles.card} ${styles.spaced}`} data-tour="uebertragen">
      <h2 className={styles.title} style={{ fontSize: "1.15rem" }}>
        Übertragen
      </h2>
      <p className={styles.subtitle}>
        Lehrgänge zwischen zwei Umgebungen bewegen – als Datei, die durch Ihre Hände geht.
        Die beiden Umgebungen reden nicht miteinander: hier herunterladen, dort hochladen.
      </p>

      <h3 className={styles.title} style={{ fontSize: "1rem" }}>
        Herunterladen
      </h3>
      <p className={styles.subtitle}>
        In die Datei kommen alle <strong>nicht stillgelegten</strong> Lehrgänge mit ihrer{" "}
        <strong>zuletzt veröffentlichten Fassung</strong> – Stammdaten, „baut auf“,
        Tastatur, Finger, Sterne, Lektionen und Test. Die Schlüssel und das Kürzel bleiben
        dabei unverändert: es ist derselbe Lehrgang in einer anderen Umgebung, keine
        Kopie.
      </p>
      {/* „Ergebnisse“ und nicht „Fortschritt“: der Wächtertest in
          `admin.draft.spec.ts` verbietet das Wort auf jeder Seite der Admin-App –
          es ist sein Merkmal dafür, dass hier eine Schülerdatenansicht steht, wo
          keine sein darf. Hier stünde es in einem Satz, der das Gegenteil sagt,
          aber ein Wächter, der Prosa auslegen muss, ist keiner mehr. */}
      <p className={styles.subtitle}>
        Nicht mit gehen: Entwürfe, die Fassungshistorie, Klassen, Ergebnisse und Lizenzen.
      </p>

      <div className={`${styles.row} ${styles.spaced}`}>
        <button
          type="button"
          className={styles.button}
          disabled={exportieren.isPending}
          onClick={() => {
            exportieren.mutate(window.location.host, {
              onSuccess: (ergebnis) => {
                download(
                  transferFileName(new Date(ergebnis.file.exportedAt)),
                  JSON.stringify(ergebnis.file, null, 2),
                );
              },
            });
          }}
        >
          {exportieren.isPending ? "Wird gelesen …" : "Datei erzeugen und herunterladen"}
        </button>
      </div>

      {exportieren.isError && (
        <div className={`${styles.notice} ${styles.noticeError} ${styles.spaced}`}>
          Die Lehrgänge konnten nicht gelesen werden.
        </div>
      )}

      {exportieren.data && (
        <div className={`${styles.notice} ${styles.noticeGood} ${styles.spaced}`}>
          {exportieren.data.file.curricula.length === 1
            ? "Ein Lehrgang steht in der Datei"
            : `${exportieren.data.file.curricula.length} Lehrgänge stehen in der Datei`}
          {exportieren.data.file.curricula.length > 0 && (
            <>
              {": "}
              {exportieren.data.file.curricula
                .map((c) => `${c.title} (Fassung ${c.sourceVersion})`)
                .join(", ")}
            </>
          )}
          .
          {exportieren.data.skipped.length > 0 && (
            <>
              {" Nicht dabei: "}
              {exportieren.data.skipped.map((s) => `${s.title} – ${s.reason}`).join(", ")}
              .
            </>
          )}
        </div>
      )}

      <h3 className={styles.title} style={{ fontSize: "1rem" }}>
        Hochladen
      </h3>
      <p className={styles.subtitle}>
        Aus der Datei entsteht je Lehrgang ein <strong>Entwurf</strong> – veröffentlicht
        wird nichts. Das geschieht wie immer in der Fassungsleiste des Lehrgangs, und nur
        dort. Vorhandene Lehrgänge bekommen ihre Stammdaten nachgezogen; Kürzel und
        Stilllegung bleiben, wie sie hier sind.
      </p>

      <div className={styles.spaced}>
        <label htmlFor="transfer-datei">Datei</label>
        <input
          id="transfer-datei"
          className={styles.field}
          type="file"
          accept="application/json,.json"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) dateiWaehlen(file);
          }}
        />
      </div>

      {leseFehler.length > 0 && (
        <div className={`${styles.notice} ${styles.noticeError} ${styles.spaced}`}>
          {leseFehler.map((p) => (
            <div key={`${p.path}-${p.message}`}>{p.message}</div>
          ))}
        </div>
      )}

      {datei && (
        <p className={styles.subtitle}>
          Aus {datei.source}, erzeugt am {berlinDate(datei.exportedAt)}.
        </p>
      )}

      {datei && hier.isPending && <p className={styles.empty}>Wird geprüft …</p>}
      {datei && hier.isError && (
        <div className={`${styles.notice} ${styles.noticeError} ${styles.spaced}`}>
          Der Stand dieser Umgebung war nicht abzurufen – ohne ihn lässt sich nicht
          prüfen, was der Import anrichten würde.
        </div>
      )}

      {plan.length > 0 && (
        <table className={styles.wide}>
          <thead>
            <tr>
              <th />
              <th className={styles.stretch}>Lehrgang</th>
              <th>Hier</th>
              <th>Hinweise</th>
            </tr>
          </thead>
          <tbody>
            {plan.map((entry) => (
              <tr key={entry.slug}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={entry.title}
                    // Gesperrte lassen sich nicht ankreuzen. Ein Kästchen, das
                    // sich setzen lässt und dann doch nichts tut, ist eine
                    // Zusage, die der Bericht widerruft.
                    disabled={!isImportable(entry)}
                    checked={isImportable(entry) && !abgewaehlt.has(entry.slug)}
                    onChange={(event) => {
                      const naechste = new Set(abgewaehlt);
                      if (event.target.checked) naechste.delete(entry.slug);
                      else naechste.add(entry.slug);
                      setAbgewaehlt(naechste);
                    }}
                  />
                </td>
                <td className={styles.stretch}>
                  <strong>{entry.title}</strong>
                  <br />
                  <span className={styles.subtitle}>
                    {entry.slug} · dort Fassung {entry.sourceVersion} vom{" "}
                    {berlinDate(entry.sourcePublishedAt)}
                  </span>
                  {entry.blockers.map((grund) => (
                    <div
                      key={grund}
                      className={`${styles.notice} ${styles.noticeError} ${styles.spaced}`}
                    >
                      {grund}
                    </div>
                  ))}
                  {entry.warnings.map((grund) => (
                    <div
                      key={grund}
                      className={`${styles.notice} ${styles.noticeInfo} ${styles.spaced}`}
                    >
                      {grund}
                    </div>
                  ))}
                </td>
                <td>{wirkung(entry)}</td>
                <td>
                  {/* Hinweise sperren nicht: ein Entwurf darf welche haben.
                      Veröffentlichen kann man ihn damit nicht - das prüft die
                      Fassungsleiste noch einmal, an ihrem Ort. */}
                  {entry.hints === 0 ? "–" : entry.hints}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {plan.length > 0 && (
        <div className={`${styles.row} ${styles.spaced}`}>
          <button
            type="button"
            className={styles.button}
            disabled={gewaehlt.length === 0 || importieren.isPending}
            onClick={() => {
              importieren.mutate(
                { entries: gewaehlt, live: hier.data ?? [] },
                { onSuccess: (ergebnis) => setBericht(ergebnis) },
              );
            }}
          >
            {importieren.isPending
              ? "Wird geschrieben …"
              : gewaehlt.length === 1
                ? "Einen Lehrgang übernehmen"
                : `${gewaehlt.length} Lehrgänge übernehmen`}
          </button>
        </div>
      )}

      {importieren.isError && (
        <div className={`${styles.notice} ${styles.noticeError} ${styles.spaced}`}>
          Der Import ist abgebrochen.
        </div>
      )}

      {bericht && (
        <div className={styles.spaced}>
          {/* Ein Teilerfolg ist möglich: über PostgREST gibt es keine
              Transaktion über mehrere Aufrufe. Er wird berichtet und nicht
              verschwiegen. */}
          {bericht.map((zeile) => (
            <div
              key={zeile.slug}
              className={`${styles.notice} ${
                zeile.ok ? styles.noticeGood : styles.noticeError
              } ${styles.spaced}`}
            >
              <strong>{zeile.title}</strong> – {zeile.message}
              {zeile.ok && (
                <>
                  {" · "}
                  <Link to={coursePath(zeile.slug, "uebersicht")}>Öffnen</Link>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
